// keccak256 — the hash every cabinet uses to turn a beacon round into an outcome.
//
// The contracts derive each draw as keccak256(abi.encodePacked(randomness, id, index)). Practice
// mode reproduces those draws in the browser, so it needs the same hash with no web3 dependency
// (the site ships none, on purpose). This is the plain Keccak-f[1600] sponge with 64-bit BigInt
// lanes: slow by design and irrelevant here, because a bet hashes a handful of 96-byte inputs.
// Differential-tested against viem in casino/test/practice.test.ts.

const ZERO = BigInt(0);
const ONE = BigInt(1);
const MASK64 = (ONE << BigInt(64)) - ONE;

// Round constants for the 24 rounds of Keccak-f[1600].
const RC = [
  '0x0000000000000001', '0x0000000000008082', '0x800000000000808a', '0x8000000080008000',
  '0x000000000000808b', '0x0000000080000001', '0x8000000080008081', '0x8000000000008009',
  '0x000000000000008a', '0x0000000000000088', '0x0000000080008009', '0x000000008000000a',
  '0x000000008000808b', '0x800000000000008b', '0x8000000000008089', '0x8000000000008003',
  '0x8000000000008002', '0x8000000000000080', '0x000000000000800a', '0x800000008000000a',
  '0x8000000080008081', '0x8000000000008080', '0x0000000080000001', '0x8000000080008008',
].map(h => BigInt(h));

// Rotation offsets r[x][y], stored at index x + 5y.
const ROT = [
  0, 1, 62, 28, 27,
  36, 44, 6, 55, 20,
  3, 10, 43, 25, 39,
  41, 45, 15, 21, 8,
  18, 2, 61, 56, 14,
].map(r => BigInt(r));

function rotl(v, r) {
  if (r === ZERO) return v;
  return ((v << r) | (v >> (BigInt(64) - r))) & MASK64;
}

function keccakF(A) {
  const C = new Array(5);
  const B = new Array(25);
  for (let round = 0; round < 24; round += 1) {
    // θ
    for (let x = 0; x < 5; x += 1) C[x] = A[x] ^ A[x + 5] ^ A[x + 10] ^ A[x + 15] ^ A[x + 20];
    for (let x = 0; x < 5; x += 1) {
      const d = C[(x + 4) % 5] ^ rotl(C[(x + 1) % 5], ONE);
      for (let y = 0; y < 25; y += 5) A[x + y] ^= d;
    }
    // ρ and π
    for (let x = 0; x < 5; x += 1) {
      for (let y = 0; y < 5; y += 1) {
        B[y + 5 * ((2 * x + 3 * y) % 5)] = rotl(A[x + 5 * y], ROT[x + 5 * y]);
      }
    }
    // χ
    for (let x = 0; x < 5; x += 1) {
      for (let y = 0; y < 25; y += 5) {
        A[x + y] = B[x + y] ^ ((B[((x + 1) % 5) + y] ^ MASK64) & B[((x + 2) % 5) + y]);
      }
    }
    // ι
    A[0] ^= RC[round];
  }
}

/** Uint8Array → 0x-prefixed 32-byte keccak-256 hex. */
export function keccak256Bytes(bytes) {
  const RATE = 136; // 1088 bits
  const padded = new Uint8Array(Math.floor(bytes.length / RATE) * RATE + RATE);
  padded.set(bytes);
  padded[bytes.length] ^= 0x01; // Keccak (pre-SHA-3) padding: 0x01 … 0x80
  padded[padded.length - 1] ^= 0x80;

  const A = new Array(25).fill(ZERO);
  for (let off = 0; off < padded.length; off += RATE) {
    for (let i = 0; i < RATE / 8; i += 1) {
      let lane = ZERO;
      for (let b = 7; b >= 0; b -= 1) lane = (lane << BigInt(8)) | BigInt(padded[off + i * 8 + b]);
      A[i] ^= lane;
    }
    keccakF(A);
  }

  let out = '';
  for (let i = 0; i < 4; i += 1) {
    let lane = A[i];
    for (let b = 0; b < 8; b += 1) {
      out += Number(lane & BigInt(0xff)).toString(16).padStart(2, '0');
      lane >>= BigInt(8);
    }
  }
  return '0x' + out;
}

/** 0x-prefixed hex string → Uint8Array. */
export function hexToBytes(hex) {
  const h = String(hex).replace(/^0x/, '');
  if (h.length % 2 !== 0) throw new Error('odd-length hex');
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** keccak256 of a 0x-prefixed hex byte string, as a 0x-prefixed hex digest. */
export function keccak256Hex(hex) {
  return keccak256Bytes(hexToBytes(hex));
}
